"use client";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/ManagementHeader";
import { supabase } from "../../../supabaseClient";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import { SHIFT_TYPES, fieldsForType, positionsForType } from "../../../lib/shiftTypes";
import { FaCalendarPlus, FaClipboardList, FaDiscord, FaExclamationTriangle, FaFileMedicalAlt, FaPeopleArrows, FaUserClock, FaUsersCog } from "react-icons/fa";
import { formatProfileName } from "../../../utils/profileName";

export default function ApplicationsPortal() {
  const { isEnabled } = useFeatureFlags();
  /* ================= PERMISSIONS ================= */
  const [permissions, setPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  /* ================= APPLICATIONS ================= */
  const [applications, setApplications] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  /* ================= SHIFTS ================= */
  const [openSection, setOpenSection] = useState(() => {
    try {
      return localStorage.getItem("pguh:management:openSection");
    } catch {
      return null;
    }
  });
  const [shifts, setShifts] = useState([]);
  const [shiftListView, setShiftListView] = useState("upcoming");
  const [shiftClock, setShiftClock] = useState(() => Date.now());
  const [showModal, setShowModal] = useState(false);
  const [editingShiftId, setEditingShiftId] = useState(null);

  const [shiftForm, setShiftForm] = useState({
    shiftType: "emergency_department",
    title: "Emergency Department Shift Allocation",
    host: "",
    hospital: "Prince George University Hospital",
    location: "",
    shiftDate: "",
    shiftStartTime: "18:00",
    briefingTime: "17:50",
    admissionsTime: "18:00",
    publicJoinTime: "18:00",
    majorInjuriesAllowed: true,
    ward: "",
    dayNight: "DAY",
    durationMinutes: 180,
  });
  const [shiftSaveStatus, setShiftSaveStatus] = useState("");
  const [shiftWizardStep, setShiftWizardStep] = useState(1);
  const [discordBotStatus, setDiscordBotStatus] = useState(null);
  const [discordConfigLoading, setDiscordConfigLoading] = useState(false);

  /* ================= SHIFT CANCELLATION ================= */
  const [cancelShiftTarget, setCancelShiftTarget] = useState(null);
  const [cancelShiftReason, setCancelShiftReason] = useState("");
  const [cancelShiftStatus, setCancelShiftStatus] = useState("");
  const [cancelShiftSubmitting, setCancelShiftSubmitting] = useState(false);

  /* ================= DATIX ================= */
  const [datixReports, setDatixReports] = useState([]);
  const [selectedDatixReport, setSelectedDatixReport] = useState(null);
  const [datixModal, setDatixModal] = useState(false);

  /* ================= LEAVE REQUESTS ================= */
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [expandedLeaveId, setExpandedLeaveId] = useState(null);
  const [leaveModal, setLeaveModal] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveStaff, setLeaveStaff] = useState([]);
  const [managementLeaveStatus, setManagementLeaveStatus] = useState("");
  const [managementLeaveSaving, setManagementLeaveSaving] = useState(false);
  const [managementLeaveForm, setManagementLeaveForm] = useState({
    user_id: "",
    leave_type: "Annual Leave",
    start_date: "",
    end_date: "",
    reason: "",
    management_notes: "",
  });

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    if (!permissions) return;

    if (permissions.recruitment_applications) fetchApplications();
    if (permissions.shift_management) fetchShifts();
    if (permissions.datix_reports) fetchDatixReports();
    if (permissions.leave_requests) {
      fetchLeaveRequests();
      fetchLeaveStaff();
    }

    const allowedSections = [
      permissions.recruitment_applications && "applications",
      permissions.shift_management && "shifts",
      permissions.datix_reports && "datix",
      permissions.leave_requests && "leaveRequests",
    ].filter(Boolean);

    let savedSection = null;
    try {
      savedSection = localStorage.getItem("pguh:management:openSection");
    } catch {
      savedSection = null;
    }

    setOpenSection(
      savedSection && allowedSections.includes(savedSection)
        ? savedSection
        : allowedSections[0] || null,
    );
  }, [permissions]);

  useEffect(() => {
    if (!openSection) return;
    try {
      localStorage.setItem("pguh:management:openSection", openSection);
    } catch {
      // localStorage may be unavailable in restricted browser modes.
    }
  }, [openSection]);


  useEffect(() => {
    const timer = window.setInterval(() => setShiftClock(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  /* ================= PERMISSIONS LOGIC ================= */

  const loadPermissions = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setPermissionsLoading(false);
      return;
    }

    const { data } = await supabase
      .from("profiles")
      .select(
        `
        management_portal,
        recruitment_applications,
        shift_management,
        datix_reports,
        leave_requests
      `,
      )
      .eq("id", user.id)
      .single();

    setPermissions(data || null);
    setPermissionsLoading(false);
  };

  /* ================= APPLICATION LOGIC ================= */

  const fetchApplications = async () => {
    const { data } = await supabase
      .from("applications")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setApplications(data);
  };

  const handleDecision = async (app, result) => {
    if (loadingId) return;

    if (
      !window.confirm(
        `Are you sure you want to ${result.toLowerCase()} this application?`,
      )
    )
      return;

    setLoadingId(app.id);

    try {
      await supabase
        .from("applications")
        .update({ status: result })
        .eq("id", app.id);

      await fetch("/api/applicationEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...app, result }),
      });

      setApplications((prev) =>
        prev.map((a) => (a.id === app.id ? { ...a, status: result } : a)),
      );
    } finally {
      setLoadingId(null);
    }
  };

  const statusBadge = (status) => {
    if (status === "Accepted") return "bg-green-600 text-white";
    if (status === "Rejected") return "bg-red-600 text-white";
    return "bg-yellow-400 text-black";
  };

  /* ================= SHIFTS LOGIC ================= */

  const fetchShifts = async () => {
    const { data, error } = await supabase
      .from("shifts")
      .select("*")
      .order("time", { ascending: true });
    if (error) console.error("Unable to load shifts:", error);
    if (data) setShifts(data);
  };

  const categorisedShifts = useMemo(() => {
    const now = shiftClock;
    const groups = { upcoming: [], ongoing: [], past: [] };

    shifts.forEach((shift) => {
      const start = new Date(shift.time).getTime();
      const durationMinutes = Math.max(1, Number(shift.duration_minutes) || 120);
      const end = start + durationMinutes * 60 * 1000;

      if (!Number.isFinite(start)) {
        groups.past.push(shift);
      } else if (now < start) {
        groups.upcoming.push(shift);
      } else if (now < end && shift.shift_status !== "Cancelled") {
        groups.ongoing.push(shift);
      } else {
        groups.past.push(shift);
      }
    });

    groups.upcoming.sort((a, b) => new Date(a.time) - new Date(b.time));
    groups.ongoing.sort((a, b) => new Date(a.time) - new Date(b.time));
    groups.past.sort((a, b) => new Date(b.time) - new Date(a.time));
    return groups;
  }, [shifts, shiftClock]);

  const visibleShifts = categorisedShifts[shiftListView] || [];

  const syncShiftDiscord = async (shiftId) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error("You are not signed in.");
    const response = await fetch("/api/shift-discord", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ shiftId }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || "Discord sync failed");
    return payload;
  };


  const loadDiscordConfig = async () => {
    setDiscordConfigLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("You are not signed in.");
      const response = await fetch("/api/shift-discord", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Unable to load Discord bot configuration");
      setDiscordBotStatus(payload);
    } catch (error) {
      setDiscordBotStatus({ ok: false, error: error.message });
    } finally {
      setDiscordConfigLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingShiftId(null);
    setShiftSaveStatus("");
    setShiftForm({
      shiftType: "emergency_department",
      title: "Emergency Department Shift Allocation",
      host: "",
      hospital: "Prince George University Hospital",
      location: "",
      shiftDate: "",
      shiftStartTime: "18:00",
      briefingTime: "17:50",
      admissionsTime: "18:00",
      publicJoinTime: "18:00",
      majorInjuriesAllowed: true,
      ward: "",
      dayNight: "DAY",
      durationMinutes: 180,
    });
    setShiftWizardStep(1);
    void loadDiscordConfig();
    setShowModal(true);
  };

  const openEditModal = (shift) => {
    setEditingShiftId(shift.id);
    setShiftSaveStatus("");
    const details = shift.shift_details || {};
    const startValue = String(shift.time || "");
    setShiftForm({
      shiftType: shift.shift_type || "emergency_department",
      title: shift.title,
      host: shift.host,
      hospital: details.hospital || "Prince George University Hospital",
      location: shift.location || "",
      shiftDate: startValue.slice(0, 10),
      shiftStartTime: details.shift_start_time || startValue.slice(11, 16) || "",
      briefingTime: shift.briefing_time ? String(shift.briefing_time).slice(0, 5) : "",
      admissionsTime: shift.admissions_time ? String(shift.admissions_time).slice(0, 5) : "",
      publicJoinTime: details.public_join_time || (shift.admissions_time ? String(shift.admissions_time).slice(0, 5) : details.shift_start_time || ""),
      majorInjuriesAllowed: Boolean(shift.major_injuries_allowed),
      ward: details.ward || "",
      dayNight: details.day_night || "DAY",
      durationMinutes: Number(shift.duration_minutes) || 120,
    });
    setShiftWizardStep(2);
    void loadDiscordConfig();
    setShowModal(true);
  };

  const saveShift = async () => {
    const durationMinutes = Number(shiftForm.durationMinutes);
    const fields = fieldsForType(shiftForm.shiftType);
    if (!shiftForm.title || !shiftForm.host || !shiftForm.shiftDate || !Number.isFinite(durationMinutes) || durationMinutes < 15) {
      setShiftSaveStatus("Complete the title, host, shift date and a valid duration.");
      return;
    }
    if (fields.location && !shiftForm.location.trim()) {
      setShiftSaveStatus("Enter a location for this shift.");
      return;
    }
    if (fields.briefing && !shiftForm.briefingTime) {
      setShiftSaveStatus("Enter the staff briefing time.");
      return;
    }
    if (fields.admissions && !shiftForm.admissionsTime) {
      setShiftSaveStatus("Enter the patient admissions / join time.");
      return;
    }
    if (!fields.admissions && !shiftForm.publicJoinTime) {
      setShiftSaveStatus("Enter the public / patient join time.");
      return;
    }
    if (fields.ward && !shiftForm.ward.trim()) {
      setShiftSaveStatus("Enter the ward for this inpatient shift.");
      return;
    }

    const schedulingTime = fields.shiftStart
      ? (shiftForm.shiftStartTime || shiftForm.briefingTime || "00:00")
      : (shiftForm.admissionsTime || shiftForm.briefingTime || shiftForm.shiftStartTime || "00:00");

    setShiftSaveStatus("Saving shift…");
    const payload = {
      shift_type: shiftForm.shiftType,
      title: shiftForm.title,
      host: shiftForm.host,
      location: shiftForm.location.trim() || null,
      time: `${shiftForm.shiftDate}T${schedulingTime}`,
      briefing_time: fields.briefing ? (shiftForm.briefingTime || null) : null,
      admissions_time: fields.admissions ? (shiftForm.admissionsTime || null) : null,
      major_injuries_allowed: fields.majorInjuries ? Boolean(shiftForm.majorInjuriesAllowed) : false,
      shift_details: {
        shift_start_time: fields.shiftStart ? (shiftForm.shiftStartTime || null) : null,
        public_join_time: fields.admissions ? (shiftForm.admissionsTime || null) : (shiftForm.publicJoinTime || null),
        hospital: fields.hospital ? (shiftForm.hospital || "TBC") : null,
        ward: fields.ward ? shiftForm.ward.trim() : null,
        day_night: fields.dayNight ? shiftForm.dayNight : null,
      },
      duration_minutes: durationMinutes,
    };

    let shiftId = editingShiftId;
    if (editingShiftId) {
      const { error } = await supabase.from("shifts").update(payload).eq("id", editingShiftId);
      if (error) { setShiftSaveStatus(error.message); return; }
    } else {
      const { data: created, error } = await supabase
        .from("shifts")
        .insert({ ...payload, attendees: [], requests: [] })
        .select("id")
        .single();
      if (error || !created) { setShiftSaveStatus(error?.message || "Unable to create shift."); return; }
      shiftId = created.id;

      const positionRows = positionsForType(shiftForm.shiftType).map((position, index) => ({
        shift_id: shiftId,
        position_key: position.key,
        position_name: position.label,
        section_name: position.section,
        capacity: position.capacity,
        sort_order: index,
      }));
      const { error: positionError } = await supabase.from("shift_positions").insert(positionRows);
      if (positionError) { setShiftSaveStatus(`Shift created, but positions failed: ${positionError.message}`); return; }
    }

    try {
      setShiftSaveStatus("Syncing Discord Components V2 post…");
      await syncShiftDiscord(shiftId);
      setShiftSaveStatus("Saved and synced to operational + public Discord channels.");
    } catch (error) {
      setShiftSaveStatus(`Saved. Discord sync failed: ${error.message}`);
    }

    await fetchShifts();
    setTimeout(() => setShowModal(false), 500);
  };

  const forceDiscordSync = async (shiftId) => {
    try {
      await syncShiftDiscord(shiftId);
      await fetchShifts();
      alert("Discord Components V2 shift post synced.");
    } catch (error) {
      alert(`Discord sync failed: ${error.message}`);
    }
  };

  const openCancelShiftModal = (shift) => {
    setCancelShiftTarget(shift);
    setCancelShiftReason("");
    setCancelShiftStatus("");
    setCancelShiftSubmitting(false);
  };

  const cancelShift = async () => {
    if (!cancelShiftTarget?.id || cancelShiftSubmitting) return;
    const reason = cancelShiftReason.trim();
    if (!reason) {
      setCancelShiftStatus("Enter a reason for cancelling this shift.");
      return;
    }

    setCancelShiftSubmitting(true);
    setCancelShiftStatus("Cancelling shift and notifying Discord…");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("You are not signed in.");

      const response = await fetch("/api/shift-discord", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shiftId: cancelShiftTarget.id,
          action: "cancel",
          reason,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Unable to cancel shift");

      setCancelShiftStatus("Shift cancelled. Public and operational Discord notices were sent.");
      await fetchShifts();
      setTimeout(() => setCancelShiftTarget(null), 800);
    } catch (error) {
      setCancelShiftStatus(error.message);
    } finally {
      setCancelShiftSubmitting(false);
    }
  };

  const deleteShift = async (shiftId) => {
    if (!window.confirm("Permanently delete this shift? Cancellation should normally be used instead so history is retained.")) return;
    await supabase.from("shifts").delete().eq("id", shiftId);
    fetchShifts();
  };

  /* ================= DATIX LOGIC ================= */

  const fetchDatixReports = async () => {
    const { data } = await supabase
      .from("datix_reports")
      .select("*")
      .order("incident_datetime", { ascending: false });
    if (data) setDatixReports(data);
  };

  const openDatixModal = (report) => {
    setSelectedDatixReport(report);
    setDatixModal(true);
  };

  /* ================= LEAVE REQUESTS LOGIC ================= */

  const fetchLeaveRequests = async () => {
    const { data } = await supabase
      .from("leave_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setLeaveRequests(data);
  };

  const fetchLeaveStaff = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, name_prefix, email, position, job_role")
      .eq("position", "Staff")
      .order("full_name", { ascending: true });
    if (error) {
      console.error("Unable to load staff for management leave", error);
      return;
    }
    setLeaveStaff(data || []);
  };

  const createManagementLeave = async (event) => {
    event.preventDefault();
    setManagementLeaveStatus("");

    if (!managementLeaveForm.user_id || !managementLeaveForm.leave_type || !managementLeaveForm.start_date || !managementLeaveForm.end_date) {
      setManagementLeaveStatus("Choose a staff member, leave type, start date and end date.");
      return;
    }
    if (managementLeaveForm.end_date < managementLeaveForm.start_date) {
      setManagementLeaveStatus("The end date cannot be before the start date.");
      return;
    }

    const member = leaveStaff.find((row) => row.id === managementLeaveForm.user_id);
    if (!member) {
      setManagementLeaveStatus("The selected staff member could not be found.");
      return;
    }

    setManagementLeaveSaving(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const managerId = authData?.user?.id || null;
      const { error } = await supabase.from("leave_requests").insert([{
        user_id: member.id,
        full_name: formatProfileName(member, member.email || "Staff member"),
        leave_type: managementLeaveForm.leave_type,
        start_date: managementLeaveForm.start_date,
        end_date: managementLeaveForm.end_date,
        reason: managementLeaveForm.reason,
        status: "Approved",
        management_created: true,
        created_by: managerId,
        management_notes: managementLeaveForm.management_notes,
      }]);
      if (error) throw error;

      setManagementLeaveForm({
        user_id: "",
        leave_type: "Annual Leave",
        start_date: "",
        end_date: "",
        reason: "",
        management_notes: "",
      });
      setManagementLeaveStatus("Leave of absence logged successfully.");
      await fetchLeaveRequests();
    } catch (error) {
      console.error("Unable to create management leave", error);
      setManagementLeaveStatus(error?.message || "Unable to log this leave of absence.");
    } finally {
      setManagementLeaveSaving(false);
    }
  };

  const approveLeaveRequest = async (leave) => {
    await supabase
      .from("leave_requests")
      .update({ status: "Approved" })
      .eq("id", leave.id);
    fetchLeaveRequests();
  };

  const rejectLeaveRequest = async (leave) => {
    await supabase
      .from("leave_requests")
      .update({ status: "Rejected" })
      .eq("id", leave.id);
    fetchLeaveRequests();
  };

  const openLeaveModal = (request) => {
    setSelectedLeaveRequest(request);
    setLeaveModal(true);
  };


  const managementOverview = useMemo(() => {
    const now = Date.now();
    return {
      applications: applications.filter((item) => item.status === "Received").length,
      upcomingShifts: shifts.filter((item) => item.shift_status !== "Cancelled" && new Date(item.time).getTime() >= now).length,
      discordLinked: shifts.filter((item) => Boolean(item.discord_message_id)).length,
      datixOpen: datixReports.length,
      leavePending: leaveRequests.filter((item) => !item.status || item.status === "Pending").length,
    };
  }, [applications, shifts, datixReports, leaveRequests]);

  /* ================= GUARDS ================= */

  if (permissionsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!permissions || !permissions.management_portal) {
    return (
      <div className="flex flex-col min-h-screen text-left">
        <Header />
        <div className="w-full px-6 mt-6">
          <h1 className="nhsuk-heading-l">Management Portal</h1>
          <p>You do not have permission to access this portal.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen text-left">
      <Header />

      <main className="pguh-management-page">
        <section className="pguh-management-hero">
          <div>
            <span className="pguh-management-eyebrow">Operations & governance</span>
            <h1>Management Portal</h1>
            <p>Manage staffing, recruitment, incidents and workforce requests from one operational dashboard.</p>
          </div>
          <div className="pguh-management-hero__status">
            <span className={discordBotStatus?.ok ? "is-online" : "is-neutral"}></span>
            <div><strong>{discordBotStatus?.ok ? `Discord bot: ${discordBotStatus.bot?.username || "Connected"}` : "Discord shift bot"}</strong><small>{discordBotStatus?.ok ? "Ready to publish shift allocations" : "Loaded when Shift Management opens"}</small></div>
          </div>
        </section>

        <section className="pguh-management-stats" aria-label="Management overview">
          {permissions.recruitment_applications && <button onClick={() => setOpenSection("applications")}><FaClipboardList/><span><small>Applications awaiting review</small><strong>{managementOverview.applications}</strong></span></button>}
          {permissions.shift_management && <button onClick={() => setOpenSection("shifts")}><FaUserClock/><span><small>Upcoming operational shifts</small><strong>{managementOverview.upcomingShifts}</strong></span></button>}
          {permissions.shift_management && <button onClick={() => setOpenSection("shifts")}><FaDiscord/><span><small>Discord-linked shifts</small><strong>{managementOverview.discordLinked}</strong></span></button>}
          {permissions.datix_reports && <button onClick={() => setOpenSection("datix")}><FaFileMedicalAlt/><span><small>DATIX reports</small><strong>{managementOverview.datixOpen}</strong></span></button>}
          {permissions.leave_requests && <button onClick={() => setOpenSection("leaveRequests")}><FaCalendarPlus/><span><small>Leave requests pending</small><strong>{managementOverview.leavePending}</strong></span></button>}
        </section>

        <nav className="pguh-management-section-tabs" aria-label="Management areas">
          {permissions.recruitment_applications && <button className={openSection === "applications" ? "is-active" : ""} onClick={() => setOpenSection("applications")}><FaClipboardList/> Recruitment</button>}
          {permissions.shift_management && <button className={openSection === "shifts" ? "is-active" : ""} onClick={() => setOpenSection("shifts")}><FaPeopleArrows/> Shift Management</button>}
          {permissions.datix_reports && <button className={openSection === "datix" ? "is-active" : ""} onClick={() => setOpenSection("datix")}><FaExclamationTriangle/> DATIX</button>}
          {permissions.leave_requests && <button className={openSection === "leaveRequests" ? "is-active" : ""} onClick={() => setOpenSection("leaveRequests")}><FaUsersCog/> Leave Requests</button>}
        </nav>

        <div className="pguh-management-content">

        {/* ================= APPLICATIONS ================= */}
        {permissions.recruitment_applications && (
          <section className={openSection === "applications" ? "" : "pguh-management-section--hidden"}>
            <div className="pguh-management-section-heading"><div><span>People</span><h2>Recruitment Applications</h2></div><small>Review and process submitted applications.</small></div>

            {openSection === "applications" && (
              <div className="space-y-4 mt-4">
                {applications.map((app) => (
                  <div key={app.id} className="border rounded">
                    <button
                      onClick={() =>
                        setExpandedId(expandedId === app.id ? null : app.id)
                      }
                      className="w-full flex justify-between items-start p-4 bg-gray-100"
                    >
                      <div>
                        <strong>{app.full_name}</strong> — {app.job_title}
                        <div className="text-sm text-gray-600">
                          Submitted{" "}
                          {new Date(app.created_at).toLocaleString("en-GB")}
                        </div>
                      </div>

                      <span
                        className={`px-3 py-1 rounded-full text-sm font-bold ${statusBadge(
                          app.status,
                        )}`}
                      >
                        {app.status}
                      </span>
                    </button>

                    {expandedId === app.id && (
                      <div className="p-6 space-y-4 bg-white">
                        <div className="grid md:grid-cols-2 gap-4">
                          <p>
                            <strong>Email:</strong> {app.email}
                          </p>
                          <p>
                            <strong>Discord:</strong> {app.discord_username}
                          </p>
                          <p>
                            <strong>Roblox:</strong> {app.roblox_username}
                          </p>
                          <p>
                            <strong>User ID:</strong> {app.user_id}
                          </p>
                        </div>

                        <hr />

                        <div>
                          <h3 className="font-bold">
                            Why do you want the job?
                          </h3>
                          <p className="whitespace-pre-wrap">
                            {app.why_do_you_want_the_job}
                          </p>
                        </div>

                        <div>
                          <h3 className="font-bold">Interests in healthcare</h3>
                          <p className="whitespace-pre-wrap">
                            {app.interests_in_healthcare}
                          </p>
                        </div>

                        <div>
                          <h3 className="font-bold">Previous experience</h3>
                          <p className="whitespace-pre-wrap">
                            {app.previous_experience}
                          </p>
                        </div>

                        <div>
                          <h3 className="font-bold">
                            Why should we choose you?
                          </h3>
                          <p className="whitespace-pre-wrap">
                            {app.why_choose_you}
                          </p>
                        </div>

                        {app.status === "Received" ? (
                          <div className="flex gap-4 pt-4 flex-wrap">
                            <button
                              className="nhsuk-button"
                              disabled={loadingId === app.id}
                              onClick={() => handleDecision(app, "Accepted")}
                            >
                              Accept
                            </button>
                            <button
                              className="nhsuk-button nhsuk-button--danger"
                              disabled={loadingId === app.id}
                              onClick={() => handleDecision(app, "Rejected")}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <p className="italic text-gray-600">
                            This application has already been processed.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ================= SHIFTS ================= */}
        {permissions.shift_management && (
          <section className={openSection === "shifts" ? "" : "pguh-management-section--hidden"}>
            <div className="pguh-management-section-heading"><div><span>Operations</span><h2>Shift Management</h2></div><small>Publish operational shifts and manage live Discord allocations.</small></div>

            {openSection === "shifts" && (
              <div className="mt-4 space-y-4">
                <div className="pguh-shift-management-toolbar">
                  <div>
                    <h2 className="nhsuk-heading-m">Operational shifts</h2>
                    <p className="nhsuk-body-s">Create typed shifts, monitor self-claimed staffing and keep the linked Discord Components V2 post in sync.</p>
                  </div>
                  {isEnabled("feature.management_shift_creation") && <button className="nhsuk-button" onClick={openCreateModal}>Create Shift</button>}
                </div>

                <div className="pguh-shift-list-tabs" role="tablist" aria-label="Shift status views">
                  {[
                    ["upcoming", "Upcoming shifts"],
                    ["ongoing", "Ongoing shifts"],
                    ["past", "Past shifts"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={shiftListView === key}
                      className={shiftListView === key ? "is-active" : ""}
                      onClick={() => setShiftListView(key)}
                    >
                      <span>{label}</span>
                      <strong>{categorisedShifts[key].length}</strong>
                    </button>
                  ))}
                </div>

                {shifts.length === 0 ? (
                  <div className="pguh-shift-admin-card"><p>No shifts have been created.</p></div>
                ) : visibleShifts.length === 0 ? (
                  <div className="pguh-shift-admin-card pguh-shift-admin-card--empty">
                    <p>No {shiftListView === "ongoing" ? "ongoing" : shiftListView} shifts.</p>
                  </div>
                ) : visibleShifts.map((shift) => (
                  <article key={shift.id} className={`pguh-shift-admin-card ${shift.shift_status === "Cancelled" ? "is-cancelled" : ""}`}>
                    <div className="pguh-shift-admin-card__header">
                      <div>
                        <span className="pguh-shift-type-badge">{SHIFT_TYPES[shift.shift_type]?.label || "Emergency Department Shift Allocation"}</span>
                        <h3>{shift.title}</h3>
                        <p>{new Date(shift.time).toLocaleString("en-GB")} · {shift.location}</p>
                      </div>
                      <div className="pguh-shift-admin-card__badges">
                        {shift.shift_status === "Cancelled" && <span className="pguh-shift-cancelled-badge">Cancelled</span>}
                        <span className={`pguh-discord-sync-badge ${shift.discord_message_id ? "is-synced" : "is-pending"}`}>
                          {shift.discord_message_id ? "Discord linked" : "Discord not linked"}
                        </span>
                      </div>
                    </div>

                    <dl className="pguh-shift-admin-grid">
                      <div><dt>Host</dt><dd>{shift.host}</dd></div>
                      <div><dt>Duration</dt><dd>{Number(shift.duration_minutes) || 120} minutes</dd></div>
                      <div><dt>Briefing</dt><dd>{shift.briefing_time ? String(shift.briefing_time).slice(0,5) : "TBC"}</dd></div>
                      <div><dt>Admissions</dt><dd>{shift.admissions_time ? String(shift.admissions_time).slice(0,5) : "TBC"}</dd></div>
                      <div><dt>Major injuries</dt><dd>{shift.major_injuries_allowed ? "Allowed" : "Not allowed"}</dd></div>
                      <div><dt>Discord message ID</dt><dd className="pguh-code-value">{shift.discord_message_id || "Not created"}</dd></div>
                    </dl>

                    {shift.shift_status === "Cancelled" && (
                      <div className="pguh-shift-cancellation-summary">
                        <strong>Shift cancelled</strong>
                        <span>{shift.cancellation_reason || "No cancellation reason recorded."}</span>
                        {shift.cancelled_at && <small>{new Date(shift.cancelled_at).toLocaleString("en-GB")}</small>}
                      </div>
                    )}
                    {shift.discord_sync_error && <div className="pguh-shift-sync-error">Discord sync: {shift.discord_sync_error}</div>}

                    <div className="flex gap-3 pt-3 flex-wrap">
                      {isEnabled("feature.management_shift_editing") && <button className="nhsuk-button nhsuk-button--secondary" onClick={() => openEditModal(shift)} disabled={shift.shift_status === "Cancelled"}>Edit details</button>}
                      {isEnabled("feature.discord_shift_publishing") && <button className="nhsuk-button nhsuk-button--secondary" onClick={() => forceDiscordSync(shift.id)}>Force Discord resync</button>}
                      {shift.shift_status !== "Cancelled" && isEnabled("feature.management_shift_cancellation") && <button className="nhsuk-button nhsuk-button--danger" onClick={() => openCancelShiftModal(shift)}>Cancel shift</button>}
                      <button className="pguh-text-button pguh-delete-shift-link" onClick={() => deleteShift(shift.id)}>Permanently delete</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ================= DATIX REPORTS ================= */}
        {permissions.datix_reports && (
          <section className={openSection === "datix" ? "" : "pguh-management-section--hidden"}>
            <div className="pguh-management-section-heading"><div><span>Safety</span><h2>DATIX Reports</h2></div><small>Review reported incidents and safety events.</small></div>

            {openSection === "datix" && (
              <div className="mt-4 space-y-4">
                {datixReports.length === 0 ? (
                  <p>No DATIX reports found.</p>
                ) : (
                  <div className="border rounded">
                    <table className="w-full table-auto">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2">Title</th>
                          <th className="text-left p-2">Incident Date</th>
                          <th className="text-left p-2">Category</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {datixReports.map((report) => (
                          <tr key={report.id} className="border-t">
                            <td className="p-2">{report.title}</td>
                            <td className="p-2">
                              {new Date(
                                report.incident_datetime,
                              ).toLocaleString("en-GB")}
                            </td>
                            <td className="p-2">{report.category}</td>
                            <td className="p-2">
                              <button
                                className="nhsuk-button nhsuk-button--secondary"
                                onClick={() => openDatixModal(report)}
                              >
                                Open Report
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* ================= LEAVE REQUESTS ================= */}
        {permissions.leave_requests && (
          <section className={openSection === "leaveRequests" ? "" : "pguh-management-section--hidden"}>
            <div className="pguh-management-section-heading"><div><span>Workforce</span><h2>Leave Requests</h2></div><small>Review staff leave requests and decisions.</small></div>

            {openSection === "leaveRequests" && (
              <div className="mt-4 space-y-4">
                <div className="ruht-panel">
                  <div className="pguh-management-section-heading">
                    <div><span>Management action</span><h3>Log staff leave of absence</h3></div>
                    <small>Create an approved leave record on behalf of a staff member.</small>
                  </div>
                  <form onSubmit={createManagementLeave} className="ruht-form-grid mt-4">
                    <label className="full">Staff member
                      <select
                        className="nhsuk-select"
                        value={managementLeaveForm.user_id}
                        onChange={(e) => setManagementLeaveForm({ ...managementLeaveForm, user_id: e.target.value })}
                        required
                      >
                        <option value="">Choose a staff member</option>
                        {leaveStaff.map((staff) => (
                          <option key={staff.id} value={staff.id}>
                            {formatProfileName(staff, staff.email || "Staff member")}{staff.job_role ? ` — ${staff.job_role}` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>Leave type
                      <select
                        className="nhsuk-select"
                        value={managementLeaveForm.leave_type}
                        onChange={(e) => setManagementLeaveForm({ ...managementLeaveForm, leave_type: e.target.value })}
                      >
                        <option>Annual Leave</option>
                        <option>Authorised Absence</option>
                        <option>Compassionate Leave</option>
                        <option>Medical Leave</option>
                        <option>Maternity Leave</option>
                        <option>Paternity Leave</option>
                        <option>Training / Study Leave</option>
                        <option>Other</option>
                      </select>
                    </label>
                    <label>Start date
                      <input className="nhsuk-input" type="date" value={managementLeaveForm.start_date} onChange={(e) => setManagementLeaveForm({ ...managementLeaveForm, start_date: e.target.value })} required />
                    </label>
                    <label>End date
                      <input className="nhsuk-input" type="date" value={managementLeaveForm.end_date} onChange={(e) => setManagementLeaveForm({ ...managementLeaveForm, end_date: e.target.value })} required />
                    </label>
                    <label className="full">Reason / description
                      <textarea className="nhsuk-textarea" rows="3" value={managementLeaveForm.reason} onChange={(e) => setManagementLeaveForm({ ...managementLeaveForm, reason: e.target.value })} />
                    </label>
                    <label className="full">Management notes
                      <textarea className="nhsuk-textarea" rows="3" value={managementLeaveForm.management_notes} onChange={(e) => setManagementLeaveForm({ ...managementLeaveForm, management_notes: e.target.value })} placeholder="Management notes about this leave record" />
                    </label>
                    <div className="full flex gap-3 items-center flex-wrap">
                      {isEnabled("feature.management_leave_logging") && <button className="nhsuk-button" type="submit" disabled={managementLeaveSaving}>
                        {managementLeaveSaving ? "Logging leave..." : "Log leave of absence"}
                      </button>}
                      {managementLeaveStatus && <span role="status">{managementLeaveStatus}</span>}
                    </div>
                  </form>
                </div>

                {leaveRequests.length === 0 ? (
                  <p>No leave requests found.</p>
                ) : (
                  leaveRequests.map((leave) => (
                    <div key={leave.id} className="border rounded">
                      <button
                        onClick={() =>
                          setExpandedLeaveId(
                            expandedLeaveId === leave.id ? null : leave.id,
                          )
                        }
                        className="w-full flex justify-between items-start p-4 bg-gray-100"
                      >
                        <div>
                          <strong>{leave.full_name}</strong> —{" "}
                          {leave.leave_type}
                          {leave.management_created && <span className="ml-2 px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-900">Management logged</span>}
                          <div className="text-sm text-gray-600">
                            Submitted{" "}
                            {new Date(leave.created_at).toLocaleString("en-GB")}
                          </div>
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-sm font-bold ${
                            leave.status === "Approved"
                              ? "bg-green-600 text-white"
                              : leave.status === "Rejected"
                                ? "bg-red-600 text-white"
                                : "bg-yellow-400 text-black"
                          }`}
                        >
                          {leave.status}
                        </span>
                      </button>

                      {expandedLeaveId === leave.id && (
                        <div className="p-6 space-y-4 bg-white">
                          <div className="grid md:grid-cols-2 gap-4">
                            <p>
                              <strong>Start Date:</strong> {leave.start_date}
                            </p>
                            <p>
                              <strong>End Date:</strong> {leave.end_date}
                            </p>
                            <p>
                              <strong>Reason:</strong> {leave.reason || "Not provided"}
                            </p>
                            {leave.management_notes && (
                              <p>
                                <strong>Management notes:</strong> {leave.management_notes}
                              </p>
                            )}
                          </div>

                          <div className="flex gap-4 pt-4 flex-wrap">
                            {leave.status === "Pending" ? (
                              <>
                                <button
                                  className="nhsuk-button"
                                  onClick={() => approveLeaveRequest(leave)}
                                >
                                  Approve
                                </button>
                                <button
                                  className="nhsuk-button nhsuk-button--danger"
                                  onClick={() => rejectLeaveRequest(leave)}
                                >
                                  Reject
                                </button>
                              </>
                            ) : (
                              <p className="italic text-gray-600">
                                This leave request has already been processed.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}
        </div>
      </main>

      {/* ================= MODALS ================= */}

      {/* Shift Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center pguh-modal-layer" onClick={() => setShowModal(false)}>
          <div className="pguh-shift-wizard" onClick={(e) => e.stopPropagation()}>
            <header className="pguh-shift-wizard__header">
              <div>
                <span className="pguh-public-kicker">Operational staffing</span>
                <h2>{editingShiftId ? "Edit operational shift" : "Create operational shift"}</h2>
                <p>{editingShiftId ? "Update the shift details and re-sync the existing Discord allocation post." : "Build the shift, staffing template and Discord publication in one workflow."}</p>
              </div>
              <button type="button" className="pguh-shift-wizard__close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </header>

            <div className="pguh-shift-wizard__steps" aria-label="Shift creation progress">
              <button className={shiftWizardStep === 1 ? "is-active" : shiftWizardStep > 1 ? "is-complete" : ""} onClick={() => !editingShiftId && setShiftWizardStep(1)}><span>1</span><div><strong>Shift type</strong><small>Choose service</small></div></button>
              <button className={shiftWizardStep === 2 ? "is-active" : shiftWizardStep > 2 ? "is-complete" : ""} onClick={() => setShiftWizardStep(2)}><span>2</span><div><strong>Shift details</strong><small>Date, times & host</small></div></button>
              <button className={shiftWizardStep === 3 ? "is-active" : ""} onClick={() => setShiftWizardStep(3)}><span>3</span><div><strong>Staffing & Discord</strong><small>Positions and publish</small></div></button>
            </div>

            <div className="pguh-shift-wizard__body">
              {shiftWizardStep === 1 && (
                <section className="pguh-shift-type-picker">
                  <div className="pguh-shift-wizard__section-heading"><h3>Select the shift type</h3><p>The selected service controls the fields, staffing positions and Discord Components V2 layout.</p></div>
                  <div className="pguh-shift-type-picker__grid">
                    {Object.entries(SHIFT_TYPES).map(([key, config]) => (
                      <button key={key} type="button" className={shiftForm.shiftType === key ? "is-selected" : ""} onClick={() => setShiftForm((prev) => ({ ...prev, shiftType: key, title: config.defaultTitle }))}>
                        <span className="pguh-shift-type-picker__icon"><FaPeopleArrows /></span>
                        <div><strong>{config.label}</strong><small>{config.positions.length} allocation positions</small></div>
                        <span className="pguh-shift-type-picker__check">{shiftForm.shiftType === key ? "✓" : "›"}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {shiftWizardStep === 2 && (() => {
                const fields = fieldsForType(shiftForm.shiftType);
                return (
                  <section>
                    <div className="pguh-shift-wizard__section-heading"><h3>{SHIFT_TYPES[shiftForm.shiftType]?.label}</h3><p>Enter the operational details that will appear to staff and in Discord.</p></div>
                    <div className="pguh-shift-form-grid pguh-shift-form-grid--advanced">
                      <div className="nhsuk-form-group pguh-shift-form-field--full"><label className="nhsuk-label" htmlFor="shift-title">Shift title</label><input id="shift-title" className="nhsuk-input" value={shiftForm.title} onChange={(e) => setShiftForm({ ...shiftForm, title: e.target.value })}/><div className="nhsuk-hint">Defaults to the selected service name, but can be changed for a special session.</div></div>
                      <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-host">Host</label><input id="shift-host" className="nhsuk-input" placeholder="Staff full name" value={shiftForm.host} onChange={(e) => setShiftForm({ ...shiftForm, host: e.target.value })}/><div className="nhsuk-hint">If their profile has a Discord ID, the bot will mention them.</div></div>
                      <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-date">Shift date</label><input id="shift-date" type="date" className="nhsuk-input" value={shiftForm.shiftDate} onChange={(e) => setShiftForm({ ...shiftForm, shiftDate: e.target.value })}/></div>

                      {fields.hospital && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-hospital">Hospital</label><select id="shift-hospital" className="nhsuk-select" value={shiftForm.hospital} onChange={(e) => setShiftForm({ ...shiftForm, hospital: e.target.value })}><option value="Prince George University Hospital">Prince George University Hospital</option><option value="Duke of York Hospital">Duke of York Hospital</option><option value="TBC">TBC</option></select></div>}
                      {fields.location && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-location">Shift location</label><input id="shift-location" className="nhsuk-input" placeholder="e.g. Emergency Department, SAU, UTC, Ward 4A" value={shiftForm.location} onChange={(e) => setShiftForm({ ...shiftForm, location: e.target.value })}/><div className="nhsuk-hint">Shown to staff and included in the Discord shift post.</div></div>}
                      {fields.shiftStart && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-start-time">Shift start time</label><input id="shift-start-time" type="time" className="nhsuk-input" value={shiftForm.shiftStartTime} onChange={(e) => setShiftForm({ ...shiftForm, shiftStartTime: e.target.value })}/></div>}
                      {fields.briefing && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-briefing">Staff briefing time</label><input id="shift-briefing" type="time" className="nhsuk-input" value={shiftForm.briefingTime} onChange={(e) => setShiftForm({ ...shiftForm, briefingTime: e.target.value })}/></div>}
                      {fields.admissions && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-admissions">Patient admissions time</label><input id="shift-admissions" type="time" className="nhsuk-input" value={shiftForm.admissionsTime} onChange={(e) => setShiftForm({ ...shiftForm, admissionsTime: e.target.value })}/></div>}
                      {!fields.admissions && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-public-join">Public / patient join time</label><input id="shift-public-join" type="time" className="nhsuk-input" value={shiftForm.publicJoinTime} onChange={(e) => setShiftForm({ ...shiftForm, publicJoinTime: e.target.value })}/><div className="nhsuk-hint">The public Discord start notification is sent at this time.</div></div>}
                      {fields.dayNight && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-day-night">Shift period</label><select id="shift-day-night" className="nhsuk-select" value={shiftForm.dayNight} onChange={(e) => setShiftForm({ ...shiftForm, dayNight: e.target.value })}><option value="DAY">DAY</option><option value="NIGHT">NIGHT</option></select></div>}
                      {fields.ward && <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-ward">Ward</label><input id="shift-ward" className="nhsuk-input" placeholder="Ward name" value={shiftForm.ward} onChange={(e) => setShiftForm({ ...shiftForm, ward: e.target.value })}/></div>}
                      <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="shift-duration-minutes">Duration</label><div className="pguh-duration-input"><input id="shift-duration-minutes" type="number" min="15" max="1440" step="15" className="nhsuk-input" value={shiftForm.durationMinutes} onChange={(e) => setShiftForm({ ...shiftForm, durationMinutes: e.target.value })}/><span>minutes</span></div></div>
                      {fields.majorInjuries && <div className="nhsuk-form-group pguh-shift-form-field--full"><label className="pguh-checkbox-row pguh-checkbox-row--prominent"><input type="checkbox" checked={shiftForm.majorInjuriesAllowed} onChange={(e) => setShiftForm({ ...shiftForm, majorInjuriesAllowed: e.target.checked })}/><span><strong>Major injuries allowed</strong><small>This will be displayed prominently in the Emergency Department Components V2 post.</small></span></label></div>}
                    </div>
                  </section>
                );
              })()}

              {shiftWizardStep === 3 && (
                <section className="pguh-shift-review-grid">
                  <div>
                    <div className="pguh-shift-wizard__section-heading"><h3>Staffing template</h3><p>These roles are created automatically and can be claimed directly by staff. Staff may claim multiple roles without approval.</p></div>
                    <div className="pguh-position-preview pguh-position-preview--structured">
                      {[...new Set(positionsForType(shiftForm.shiftType).map((p) => p.section))].map((section) => <div key={section} className="pguh-position-section"><strong>{section}</strong>{positionsForType(shiftForm.shiftType).filter((p) => p.section === section).map((position) => <span key={position.key}>{position.label}<small>{position.capacity > 1 ? `Up to ${position.capacity}` : "1 position"}</small></span>)}</div>)}
                    </div>
                  </div>
                  <aside className="pguh-discord-publish-card">
                    <div className="pguh-discord-publish-card__brand"><FaDiscord/><div><strong>Discord bot delivery</strong><small>No webhook is used for shift posts.</small></div></div>
                    {discordConfigLoading ? <p>Checking Discord bot…</p> : discordBotStatus?.ok ? <div className="pguh-bot-connected"><span></span><div><strong>{discordBotStatus.bot?.username || "Discord bot"} connected</strong><small>{discordBotStatus.guildName || "Configured server"}</small></div></div> : <div className="pguh-bot-warning"><strong>Bot configuration unavailable</strong><small>{discordBotStatus?.error || "Set DISCORD_BOT_TOKEN and DISCORD_GUILD_ID. Shift channels are hardcoded by the application."}</small></div>}
                    <div className="pguh-discord-fixed-destinations" aria-label="Discord destinations">
                      <div><strong>Shift allocation channel</strong><small>Fixed channel · 1534598333799207154</small></div>
                      <div><strong>Public shift announcement channel</strong><small>Fixed channel · 1534597275450609684</small></div>
                    </div>
                    <div className="pguh-discord-preview"><span>@everyone · Operations</span><strong>{shiftForm.title}</strong><small>{shiftForm.shiftDate || "Shift date"} · {shiftForm.location || "Location TBC"} · Host: {shiftForm.host || "TBC"}</small></div>
                    <div className="pguh-discord-preview"><span>@everyone · Public announcement</span><strong>{shiftForm.title}</strong><small>{shiftForm.shiftDate || "Shift date"} · {shiftForm.location || "Location TBC"} · ✅ attendance reaction</small></div>
                  </aside>
                </section>
              )}

              {shiftSaveStatus && <div className="pguh-shift-save-status" role="status">{shiftSaveStatus}</div>}
            </div>

            <footer className="pguh-shift-wizard__footer">
              <div><strong>Step {shiftWizardStep} of 3</strong><small>{editingShiftId ? "Changes are only published when you save." : "Nothing is posted to Discord until the final step."}</small></div>
              <div className="pguh-shift-wizard__actions">
                <button className="nhsuk-button nhsuk-button--secondary" onClick={() => setShowModal(false)}>Cancel</button>
                {shiftWizardStep > (editingShiftId ? 2 : 1) && <button className="nhsuk-button nhsuk-button--secondary" onClick={() => setShiftWizardStep((step) => Math.max(editingShiftId ? 2 : 1, step - 1))}>Back</button>}
                {shiftWizardStep < 3 ? <button className="nhsuk-button" onClick={() => setShiftWizardStep((step) => Math.min(3, step + 1))}>Continue</button> : ((editingShiftId ? isEnabled("feature.management_shift_editing") : isEnabled("feature.management_shift_creation")) && isEnabled("feature.discord_shift_publishing") ? <button className="nhsuk-button" onClick={saveShift}>{editingShiftId ? "Save & update Discord" : "Create & publish shift"}</button> : <div className="nhsuk-inset-text">This shift write/publish action is disabled by Developer Controls.</div>)}
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* Shift Cancellation Modal */}
      {cancelShiftTarget && (
        <div className="pguh-allocation-modal-overlay" onClick={() => !cancelShiftSubmitting && setCancelShiftTarget(null)}>
          <section
            className="pguh-shift-cancel-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-shift-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pguh-shift-cancel-modal__header">
              <div>
                <span className="pguh-public-kicker">Operations</span>
                <h2 id="cancel-shift-title" className="nhsuk-heading-m">Cancel shift</h2>
              </div>
              <button type="button" className="pguh-allocation-modal__close" aria-label="Close cancellation" disabled={cancelShiftSubmitting} onClick={() => setCancelShiftTarget(null)}>×</button>
            </header>
            <div className="pguh-shift-cancel-modal__body">
              <div className="pguh-shift-cancel-warning">
                <strong>{cancelShiftTarget.title}</strong>
                <span>{new Date(cancelShiftTarget.time).toLocaleString("en-GB")} · {cancelShiftTarget.location || "Location TBC"}</span>
              </div>
              <p>This keeps the shift in history, prevents further role changes, retains existing allocations for audit/history, and sends a Components V2 cancellation notice to both Discord channels.</p>
              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="shift-cancellation-reason"><strong>Cancellation reason</strong></label>
                <div className="nhsuk-hint">This reason is shown publicly and in the operational staffing channel.</div>
                <textarea id="shift-cancellation-reason" className="nhsuk-textarea" rows="4" value={cancelShiftReason} disabled={cancelShiftSubmitting} onChange={(e) => setCancelShiftReason(e.target.value)} placeholder="e.g. Session cancelled due to insufficient staffing" />
              </div>
              {cancelShiftStatus && <div className="pguh-shift-save-status" role="status">{cancelShiftStatus}</div>}
            </div>
            <footer className="pguh-shift-cancel-modal__footer">
              <button className="nhsuk-button nhsuk-button--secondary" disabled={cancelShiftSubmitting} onClick={() => setCancelShiftTarget(null)}>Keep shift</button>
              {isEnabled("feature.management_shift_cancellation") && isEnabled("feature.discord_cancellation_notices") && <button className="nhsuk-button nhsuk-button--danger" disabled={cancelShiftSubmitting || !cancelShiftReason.trim()} onClick={cancelShift}>{cancelShiftSubmitting ? "Cancelling…" : "Cancel shift & notify Discord"}</button>}
            </footer>
          </section>
        </div>
      )}

      {/* DATIX Report Modal */}
      {datixModal && selectedDatixReport && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white p-6 rounded w-[500px] max-w-[95vw] space-y-4 overflow-y-auto max-h-[90vh]">
            <h2 className="nhsuk-heading-m">{selectedDatixReport.title}</h2>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Incident Date:</strong>{" "}
                {new Date(selectedDatixReport.incident_datetime).toLocaleString(
                  "en-GB",
                )}
              </p>
              <p>
                <strong>Location:</strong> {selectedDatixReport.location}
              </p>
              <p>
                <strong>Category:</strong> {selectedDatixReport.category}
              </p>
              <p>
                <strong>Subcategory:</strong> {selectedDatixReport.subcategory}
              </p>
              <p>
                <strong>Patient Involved:</strong>{" "}
                {selectedDatixReport.patient_involved}
              </p>
              <p>
                <strong>Injury:</strong> {selectedDatixReport.injury}
              </p>
              <p>
                <strong>Severity:</strong> {selectedDatixReport.severity}
              </p>
              <p>
                <strong>Description:</strong> {selectedDatixReport.description}
              </p>
              <p>
                <strong>Immediate Actions:</strong>{" "}
                {selectedDatixReport.immediate_actions}
              </p>
              <p>
                <strong>People Involved:</strong>{" "}
                {selectedDatixReport.people_involved}
              </p>
              <p>
                <strong>Witnesses:</strong> {selectedDatixReport.witnesses}
              </p>
              <p>
                <strong>Contributing Factors:</strong>{" "}
                {selectedDatixReport.contributing_factors}
              </p>
            </div>
            <button
              className="nhsuk-button nhsuk-button--secondary w-full mt-4"
              onClick={() => setDatixModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
